// src/pages/Integrations/MarketplaceDetails.tsx

import React, { useEffect, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import {
  Container,
  Spinner,
  Alert,
  Button,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Badge,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch, Action } from "@reduxjs/toolkit";

import ConfirmConnectModal from "../Actions/ConfirmConnectModal";
import ApiKeyModal from "../Actions/ApiKeyModal";
import IntegrationAccounts from "./IntegrationAccounts";

import {
  getAppRequest,
  listVersionsRequest,
  installRequest,
  oauthInitiateRequest,
  getAppQuotaRequest
} from "store/integrations/action";
import type { AppDTO, AppVersionDTO } from "store/integrations/types";
import type { ApplicationState } from "store";
import MarketplaceDetailsShimmer from "./MarketplaceDetailsShimmer";
import { useCompanyName } from "Components/Common/ThemeSettings/themeutitlity";

const MarketplaceDetails: React.FC<WithTranslation> = ({ t }) => {
  document.title = `DTS | ${useCompanyName()}`;
  const { id: appId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const tenantId = useSelector((s: ApplicationState) => s.auth.userProfile.tenant.id!);
  const quota = useSelector((s: ApplicationState) => s.integration.quota);
  const loadingQuota = useSelector((s: ApplicationState) => s.integration.loadingQuota);
  const errorQuota = useSelector((s: ApplicationState) => s.integration.errorQuota);


  const {
    currentApp: app,
    loadingDetail,
    errorDetail,
    versionsByAppId,
    loadingVersions,
    errorVersions,
  } = useSelector((s: ApplicationState) => ({
    currentApp: s.integration.currentApp,
    loadingDetail: s.integration.loadingDetail,
    errorDetail: s.integration.errorDetail,
    versionsByAppId: s.integration.versionsByAppId,
    loadingVersions: s.integration.loadingVersions,
    errorVersions: s.integration.errorVersions,
  }));

  // OAuth / API-key modals
  const [confirmApp, setConfirmApp] = useState<AppDTO | null>(null);
  const [apiKeyApp, setApiKeyApp] = useState<AppDTO | null>(null);
  const [oauthLoading, setOauthLoading] = useState(false);


  // Fetch on mount
  useEffect(() => {
    if (appId) {
      dispatch(getAppRequest(tenantId, appId));
      dispatch(listVersionsRequest(appId));
    }
  }, [dispatch, tenantId, appId]);

  useEffect(() => {
    if (appId && tenantId) {
      dispatch(getAppQuotaRequest(tenantId, appId));
    }
  }, [dispatch, tenantId, appId]);

  // --- early returns ---
  if (!appId) {
    return (
      <Container className="py-5 text-center" style={{ maxWidth: 480 }}>
        <Alert color="warning">
          {t("integrations.details.notFound", "Integration ID missing")}
        </Alert>
      </Container>
    );
  }
  if (loadingDetail || loadingVersions) {
    return <MarketplaceDetailsShimmer />;
  }
  const errorMsg = errorDetail || errorVersions;
  if (errorMsg) {
    return (
      <Container className="py-5" style={{ maxWidth: 480 }}>
        <Alert color="danger">{errorMsg}</Alert>
        <Button outline onClick={() => navigate("/integrations")}>
          {t("action.backToList", "Back to Integrations")}
        </Button>
      </Container>
    );
  }
  if (!app) {
    return (
      <Container className="py-5 text-center" style={{ maxWidth: 480 }}>
        <Alert color="warning">
          {t("integrations.details.notFound", "This integration could not be found.")}
        </Alert>
        <Button color="primary" onClick={() => navigate("/integrations")}>
          {t("action.backToList", "Back to Integrations")}
        </Button>
      </Container>
    );
  }

  // --- version & metadata ---
  const versions: AppVersionDTO[] = versionsByAppId[appId] ?? [];
  const version = versions[0];
  const metadata = version?.metadata as any;
  if (!version || !metadata) {
    return (
      <Container className="py-5 text-center" style={{ maxWidth: 480 }}>
        <Alert color="warning">
          {t(
            "integrations.details.noVersion",
            "No versions available for this integration."
          )}
        </Alert>
      </Container>
    );
  }

  // OAuth confirm handler
  const onConfirmOauth = () => {
    if (!confirmApp) return;
    setOauthLoading(true);
    dispatch(
      oauthInitiateRequest(
        tenantId,
        confirmApp.id,
        (url: string) => (window.location.href = url),
        (err: any) => {
          console.error(err);
          setOauthLoading(false);
          setConfirmApp(null);
        }
      )
    );
  };

  // API-key submit handler
  const onSubmitApiKey = (key: string, secret: string) => {
    if (!apiKeyApp) return;
    setApiKeyApp(null);
    dispatch(
      installRequest(
        tenantId,
        version.id,
        JSON.stringify({ apiKey: key, secret }),
        () => dispatch(getAppRequest(tenantId, appId)),
        console.error
      )
    );
  };

  return (
    <>
      {/* Modals */}
      {confirmApp && (
        <ConfirmConnectModal
          show
          onCloseClick={() => setConfirmApp(null)}
          selectedPlatform={{
            app: { name: confirmApp.name, logo_url: confirmApp.logo_url },
            config: {},
          }}
          onConfirm={onConfirmOauth}
          loading={oauthLoading}
        />
      )}
      {apiKeyApp && metadata.credentials && (
        <ApiKeyModal
          show
          appName={apiKeyApp.name}
          installName={apiKeyApp.name /* or whatever display name */}
          credentials={metadata.credentials}
          onClose={() => setApiKeyApp(null)}
          onSubmit={configJson => {
            dispatch(
              installRequest(
                tenantId,
                version.id,
                configJson?.name,
                JSON.stringify(configJson),
                () => dispatch(getAppRequest(tenantId, appId)),
                console.error
              )
            );
            setApiKeyApp(null);
          }}
        />
      )}

      <Container fluid className="py-5">
        {/* App Header */}
        <Card className="mb-4 shadow-sm border-0">
          <div className="bg-light p-4 d-flex align-items-center justify-content-between flex-wrap rounded">
            <div className="d-flex align-items-center">
              <img
                src={metadata.logo}
                alt={metadata.name}
                style={{ width: 70, height: 70, borderRadius: 10, objectFit: "cover" }}
              />
              <div className="ms-3">
                <h4 className="mb-0">{metadata.name}</h4>
                <small className="text-muted">{metadata.developer}</small>
              </div>
            </div>
            <Badge
              color={app.installations?.some((i) => i.status === "ACTIVE") ? "success" : "secondary"}
              className="px-3 py-2 rounded-pill mt-3 mt-md-0"
            >
              {app.installations?.some((i) => i.status === "ACTIVE")
                ? t("integrations.status.connected", "Connected")
                : t("integrations.status.notConnected", "Not Connected")}
            </Badge>
          </div>
        </Card>

        {/* === Split into two columns === */}
        <Row className="gx-5">
          {/* Left: Accounts + main sections */}
          <Col lg={8}>
            {/* Connected Accounts & Setups */}
            <IntegrationAccounts tenantId={tenantId} app={app} docsUrl={metadata.docsUrl} />

            {/* Overview */}
            <Card className="mb-4 shadow-sm">
              <CardHeader className="border-0 d-flex align-items-center">
                <i className="ri-information-line text-primary me-2" />
                <h5 className="mb-0">{t("integrations.details.overview", "Overview")}</h5>
              </CardHeader>
              <CardBody dangerouslySetInnerHTML={{ __html: metadata.overview }} />
            </Card>

            {/* Key Features */}
            <Card className="mb-4 shadow-sm">
              <CardHeader className="border-0 d-flex align-items-center">
                <i className="ri-star-line text-warning me-2" />
                <h5 className="mb-0">{t("integrations.details.features", "Key Features")}</h5>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {metadata.features.map((feat: string, idx: number) => (
                    <ListGroupItem key={idx} className="border-0 px-0 py-2">
                      <i className="ri-checkbox-circle-line text-success me-2" />
                      {feat}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>

            {/* Shared Data */}
            {Array.isArray(metadata.sharedData) && metadata.sharedData.length > 0 && (
              <Card className="mb-4 shadow-sm">
                <CardHeader className="border-0 d-flex align-items-center">
                  <i className="ri-share-forward-line text-info me-2" />
                  <h5 className="mb-0">{t("integrations.details.sharedData", "Shared Data")}</h5>
                </CardHeader>
                <CardBody>
                  <ListGroup flush>
                    {metadata.sharedData.map((d: any, idx: number) => (
                      <ListGroupItem
                        key={idx}
                        className="border-0 px-0 py-2 d-flex align-items-start"
                      >
                        <i
                          className={`me-2 ${d.direction === "two-way"
                            ? "ri-arrow-left-right-line text-success"
                            : "ri-arrow-left-line text-primary"
                            }`}
                        />
                        <div>
                          <strong>{d.name}</strong>
                          <div className="text-muted small">{d.description}</div>
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </CardBody>
              </Card>
            )}

            {/* Permissions */}
            {metadata.permissions && Object.keys(metadata.permissions).length > 0 && (
              <Card className="mb-4 shadow-sm">
                <CardHeader className="border-0 d-flex align-items-center">
                  <i className="ri-lock-line text-warning me-2" />
                  <h5 className="mb-0">{t("integrations.details.permissions", "Permissions")}</h5>
                </CardHeader>
                <CardBody>
                  <ListGroup flush>
                    {Object.entries(metadata.permissions).map(([key, desc], idx) => (
                      <ListGroupItem
                        key={idx}
                        className="border-0 px-0 py-2 d-flex align-items-start"
                      >
                        <i className="ri-shield-keyhole-line text-warning me-2 mt-1" />
                        <div>
                          <strong>{key}</strong>
                          <div className="text-muted small">{String(desc)}</div>
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </CardBody>
              </Card>
            )}
          </Col>


          {/* Right: Meta & Docs */}
          <Col lg={4}>
            <Card className="mb-4 shadow-sm">
              <CardHeader className="border-0">
                <h5 className="mb-0">{t("integrations.details.details", "Details")}</h5>
              </CardHeader>
              <CardBody>
                <p className="mb-2">
                  <strong>{t("integrations.details.version", "Version")}:</strong> {metadata.version}
                </p>
                <p className="mb-0">
                  <strong>{t("integrations.details.category", "Category")}:</strong> {metadata.category}
                </p>
              </CardBody>
              {/* --- Quota Details Section --- */}
              {loadingQuota ? (
                <CardBody>
                  <div className="text-center my-3">
                    <Spinner size="sm" className="me-2" /> Loading quota...
                  </div>
                </CardBody>
              ) : quota?.quotaEnabled ? (
                <CardBody>
                  <div className="mb-2 d-flex align-items-center">
                    <i className="ri-speed-up-line text-info me-2" style={{ fontSize: 22 }} />
                    <strong>Quota: </strong>
                    <span className="ms-2">
                      {quota.creditsRemaining} / {quota.creditsTotal} remaining
                    </span>
                  </div>
                  <div className="mb-1 small text-muted">
                    Used: {quota.creditsUsed} • Reset:{" "}
                    {quota.resetAt ? new Date(quota.resetAt).toLocaleDateString() : "-"}
                  </div>
                  {quota.creditsRemaining === 0 && (
                    <Alert color="danger" className="mt-2 py-2">
                      <i className="ri-error-warning-line me-1" /> Quota exceeded. Upgrade or wait for reset.
                    </Alert>
                  )}
                </CardBody>
              ) : quota && !quota.quotaEnabled ? (
                <CardBody>
                  <div className="mb-2 d-flex align-items-center">
                    <i className="ri-checkbox-circle-line text-success me-2" style={{ fontSize: 20 }} />
                    <span>This integration has unlimited usage.</span>
                  </div>
                </CardBody>
              ) : null}
              <CardBody className="text-center border-top">
                <a
                  href={metadata.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="d-inline-flex align-items-center gap-1 text-decoration-none"
                >
                  <i className="ri-book-open-line" />{" "}
                  {t("integrations.details.viewDocs", "View Documentation")}
                </a>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default withTranslation()(MarketplaceDetails);
